import type { Project, CreateProjectCommand, UpdateProjectCommand, PagedResult, ProjectUser, Role, DeleteProjectCommand } from '@/types/project';
import type { DashboardStats } from '@/types/dashboard';
import type { Folder, FolderContents, CreateFolderCommand, UpdateFolderCommand, FolderUser, FileItem } from '@/types/folder';
import type { ActivityLogEntry } from '@/types/activityLog';
import type { SearchResults } from '@/types/search';
import type { WorkstreamDto } from '@/types/workstream';
import type { CreditPackage, Payment, CheckoutSession, TransferTarget, TransferCreditsResult } from '@/types/payment';
import type { SeatPlan, OrganizationSeatSummary, OrganizationMemberSeatStatus, HasSeatAccess } from '@/types/seats';
import type {
  Organization,
  OrganizationUser,
  OrganizationUserRole,
  PendingOrganizationInvitation,
  CreateOrganizationCommand,
  UpdateOrganizationCommand,
  DeleteOrganizationCommand,
} from '@/types/organization';

// API Client for backend integration
// In development, use empty string to use relative URLs (goes through Vite proxy)
// In production, set VITE_API_URL to the backend URL
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Check if we're in mock mode (no backend configured and DEV mode disabled)
const isMockMode = false; // Always try to use backend through proxy

export interface User {
  id: number;
  email: string;
  name: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  picture?: string;
  roles?: string[];
  theme?: string;
  language?: string;
  timezone?: string;
}

export interface UserSettings {
  displayName?: string;
  theme?: string;
  language?: string;
  timezone?: string;
}

export interface UserProfileDto {
  id: number;
  email?: string;
  nickName?: string;
  firstName?: string;
  lastName?: string;
  theme?: string;
  language?: string;
  timezone?: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface AuthResponse {
  user: User;
  token?: string;
  expiresIn?: number;
}

// Endpoint used to exchange the refresh_token cookie for a fresh access token.
const REFRESH_ENDPOINT = '/api/Auth/refresh';

class ApiClient {
  private baseUrl: string;

  // Single-flight guard shared by BOTH refresh paths: the proactive timer
  // (refreshToken()) and the reactive 401-retry (tryRefreshToken()). Keycloak
  // rotates refresh tokens on every use, so two concurrent refreshes would send
  // the same token — the loser gets rejected and the backend wipes the auth
  // cookies, logging the user out mid-session. Coalescing every caller into one
  // in-flight request guarantees the refresh token is only ever used once.
  private refreshInFlight: Promise<{ ok: boolean; expiresIn?: number }> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Perform the actual refresh request, coalescing all concurrent callers
   * (proactive timer + reactive 401 retries) into a single network request.
   */
  private performRefresh(): Promise<{ ok: boolean; expiresIn?: number }> {
    if (this.refreshInFlight !== null) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = (async () => {
      try {
        const response = await fetch(`${this.baseUrl}${REFRESH_ENDPOINT}`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) {
          return { ok: false };
        }
        const data = (await response.json().catch(() => ({}))) as { expiresIn?: number };
        return { ok: true, expiresIn: data.expiresIn };
      } catch {
        return { ok: false };
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  /**
   * Attempt to refresh the access token using the refresh_token cookie.
   * Returns true if the refresh succeeded, false otherwise.
   */
  private async tryRefreshToken(): Promise<boolean> {
    return (await this.performRefresh()).ok;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<T> {
    // In mock mode, throw an error to indicate no backend
    if (isMockMode) {
      throw new Error('No backend configured');
    }

    const url = `${this.baseUrl}${endpoint}`;
    
    const config: RequestInit = {
      ...options,
      credentials: 'include', // Send cookies automatically
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        if (response.status === 401) {
          // A 401 usually means the short-lived access_token cookie expired while
          // the refresh_token is still valid. Transparently refresh once and retry
          // before giving up. Never retry the refresh endpoint itself (avoids loops).
          if (!isRetry && endpoint !== REFRESH_ENDPOINT) {
            const refreshed = await this.tryRefreshToken();
            if (refreshed) {
              return this.request<T>(endpoint, options, true);
            }
          }

          // Cookie expired or invalid and refresh failed — notify the app to redirect to login
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
          throw new Error('Unauthorized');
        }
        const error = await response.json().catch(() => ({ message: 'An error occurred' }));
        const thrown = new Error(error.message || `HTTP ${response.status}`) as Error & { code?: string };
        // Preserve the structured error code (e.g. "SeatEntitlement.Forbidden") from the
        // JSON body so callers can branch on it without re-parsing the message string.
        if (typeof error.error === 'string') {
          thrown.code = error.error;
        }
        throw thrown;
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      
      return {} as T;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error occurred: ' + JSON.stringify(error));
    }
  }

  /**
   * Authenticate with Keycloak access token.
   * Sends both access and refresh tokens; backend stores them as HttpOnly cookies.
   */
  async authenticateWithKeycloak(
    accessToken: string,
    refreshToken: string,
    expiresIn: number,
    refreshExpiresIn: number,
  ): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/api/Auth/keycloak/callback', {
      method: 'POST',
      body: JSON.stringify({ accessToken, refreshToken, expiresIn, refreshExpiresIn }),
    });
    return response;
  }

  /**
   * Refresh the access token using the stored refresh_token cookie.
   * Backend calls Keycloak and rotates both cookies.
   * Returns the new access token expiry in seconds.
   */
  async refreshToken(): Promise<{ expiresIn: number }> {
    // Route through the shared single-flight guard so the proactive timer never
    // races the reactive 401-retry path against Keycloak's refresh-token rotation.
    const result = await this.performRefresh();
    if (!result.ok || result.expiresIn === undefined) {
      throw new Error('Token refresh failed');
    }
    return { expiresIn: result.expiresIn };
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    return this.request<User>('/api/Auth/me');
  }

  /**
   * Get current user's profile from database (includes settings like theme, language)
   */
  async getCurrentUserProfile(): Promise<UserProfileDto> {
    return this.request<UserProfileDto>('/api/Users/me');
  }

  /**
   * Update the current user's settings (display name, theme, language)
   */
  async updateUserSettings(userId: number, settings: UserSettings): Promise<UserProfileDto> {
    return this.request<UserProfileDto>(`/api/Users/${userId}/settings`, {
      method: 'PATCH',
      body: JSON.stringify({
        displayName: settings.displayName ?? null,
        theme: settings.theme ?? null,
        language: settings.language ?? null,
        timezone: settings.timezone ?? null,
      }),
    });
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    // Backend will clear the HttpOnly cookie
    await this.request('/api/Auth/logout', {
      method: 'POST',
    });
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string }> {
    return this.request('/api/health');
  }

  // ─── Dashboard ────────────────────────────────────────────

  /**
   * Get aggregated dashboard statistics
   */
  async getDashboardStats(): Promise<DashboardStats> {
    return this.request<DashboardStats>('/api/Dashboard');
  }

  // ─── Projects ─────────────────────────────────────────────

  /**
   * Get active projects sorted by creation date (descending), paginated
   */
  async getActiveProjects(): Promise<PagedResult<Project>> {
    return this.request<PagedResult<Project>>(
      '/api/projects?status=0&sortBy=createdat&sortDirection=desc&page=1&pageSize=20'
    );
  }

  /**
   * Get all projects
   */
  async getProjects(params: { page?: number; pageSize?: number } = {}): Promise<PagedResult<Project>> {
    const query = new URLSearchParams();
    if (params.page) query.set('Page', String(params.page));
    if (params.pageSize) query.set('PageSize', String(params.pageSize));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request<PagedResult<Project>>(`/api/Projects${suffix}`);
  }

  /**
   * Get all project roles for the currently authenticated user
   */
  async getMyProjectRoles(): Promise<{ projectId: number; roleType: number }[]> {
    return this.request<{ projectId: number; roleType: number }[]>('/api/Projects/my-roles');
  }

  /**
   * Get the activity log for a project (newest first)
   */
  async getActivityLog(projectId: number, limit = 50): Promise<ActivityLogEntry[]> {
    return this.request<ActivityLogEntry[]>(`/api/Projects/${projectId}/activity-log?limit=${limit}`);
  }

  async search(term: string, limit = 10): Promise<SearchResults> {
    return this.request<SearchResults>(`/api/Search?q=${encodeURIComponent(term)}&limit=${limit}`);
  }

  /**
   * Get a project by ID
   */
  async getProject(id: number): Promise<Project> {
    return this.request<Project>(`/api/Projects/${id}`);
  }

  /**
   * Create a new project
   */
  async createProject(command: CreateProjectCommand): Promise<Project> {
    return this.request<Project>('/api/Projects', {
      method: 'POST',
      body: JSON.stringify(command),
    });
  }

  /**
   * Update an existing project
   */
  async updateProject(id: number, command: UpdateProjectCommand): Promise<Project> {
    return this.request<Project>(`/api/Projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(command),
    });
  }

  /**
   * Delete a project
   */
  async deleteProject(id: number, command: DeleteProjectCommand): Promise<void> {
    return this.request<void>(`/api/Projects/${id}?rowVersion=${command.rowVersion}`, {
      method: 'DELETE',
    });
  }

  /**
   * Trigger report generation for a project
   */
  async generateReport(projectId: number): Promise<Project> {
    return this.request<Project>(`/api/Projects/${projectId}/generate-report`, {
      method: 'POST',
    });
  }

  // ─── Organizations ─────────────────────────────────────────

  async getOrganizations(params: {
    name?: string;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
    page?: number;
    pageSize?: number;
  } = {}): Promise<PagedResult<Organization>> {
    const query = new URLSearchParams();
    if (params.name) query.set('Name', params.name);
    if (params.sortBy) query.set('SortBy', params.sortBy);
    if (params.sortDirection) query.set('SortDirection', params.sortDirection);
    if (params.page) query.set('Page', String(params.page));
    if (params.pageSize) query.set('PageSize', String(params.pageSize));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request<PagedResult<Organization>>(`/api/Organizations${suffix}`);
  }

  async getMyOrganizationRoles(): Promise<OrganizationUserRole[]> {
    return this.request<OrganizationUserRole[]>('/api/Organizations/my-roles');
  }

  async getOrganization(id: number): Promise<Organization> {
    return this.request<Organization>(`/api/Organizations/${id}`);
  }

  async createOrganization(command: CreateOrganizationCommand): Promise<Organization> {
    return this.request<Organization>('/api/Organizations', {
      method: 'POST',
      body: JSON.stringify(command),
    });
  }

  async updateOrganization(id: number, command: UpdateOrganizationCommand): Promise<Organization> {
    return this.request<Organization>(`/api/Organizations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(command),
    });
  }

  async deleteOrganization(id: number, command: DeleteOrganizationCommand): Promise<void> {
    return this.request<void>(`/api/Organizations/${id}?rowVersion=${command.rowVersion}`, {
      method: 'DELETE',
    });
  }

  async addProjectToOrganization(organizationId: number, projectId: number): Promise<void> {
    return this.request<void>(`/api/Organizations/${organizationId}/projects`, {
      method: 'POST',
      body: JSON.stringify({ projectId }),
    });
  }

  /**
   * Get a paginated, sorted list of the projects belonging to an organization
   */
  async getOrganizationProjects(
    organizationId: number,
    params: { page?: number; pageSize?: number; sortBy?: string; sortDirection?: 'asc' | 'desc' } = {}
  ): Promise<PagedResult<Project>> {
    const query = new URLSearchParams();
    if (params.page) query.set('Page', String(params.page));
    if (params.pageSize) query.set('PageSize', String(params.pageSize));
    if (params.sortBy) query.set('SortBy', params.sortBy);
    if (params.sortDirection) query.set('SortDirection', params.sortDirection);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request<PagedResult<Project>>(`/api/Organizations/${organizationId}/projects${suffix}`);
  }

  /**
   * Search for projects the current user may move into this organization
   */
  async getAssignableProjects(organizationId: number, search?: string, limit = 20): Promise<Project[]> {
    const query = new URLSearchParams();
    if (search) query.set('search', search);
    if (limit) query.set('limit', String(limit));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request<Project[]>(`/api/Organizations/${organizationId}/assignable-projects${suffix}`);
  }

  // ─── Roles ────────────────────────────────────────────────

  /**
   * Get all available roles (Owner, Editor, Viewer)
   */
  async getRoles(): Promise<Role[]> {
    return this.request<Role[]>('/api/Roles');
  }

  // ─── Project Users ────────────────────────────────────────

  /**
   * Get all users associated with a project
   */
  async getProjectUsers(projectId: number): Promise<ProjectUser[]> {
    return this.request<ProjectUser[]>(`/api/Projects/${projectId}/users`);
  }

  /**
   * Assign an existing user to a project by email (auto-assigned Viewer role)
   */
  async assignUserToProject(projectId: number, email: string): Promise<ProjectUser> {
    return this.request<ProjectUser>(`/api/Projects/${projectId}/users`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  /**
   * Change a user's role on a project
   */
  async updateProjectUserRole(projectId: number, userId: number, roleId: number): Promise<ProjectUser> {
    return this.request<ProjectUser>(`/api/Projects/${projectId}/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ roleId }),
    });
  }

  /**
   * Remove a user from a project
   */
  async removeProjectUser(projectId: number, userId: number): Promise<void> {
    return this.request<void>(`/api/Projects/${projectId}/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // ─── Organization Users ────────────────────────────────────

  async getOrganizationUsers(organizationId: number): Promise<OrganizationUser[]> {
    return this.request<OrganizationUser[]>(`/api/Organizations/${organizationId}/users`);
  }

  async inviteUserToOrganization(organizationId: number, email: string): Promise<OrganizationUser> {
    return this.request<OrganizationUser>(`/api/Organizations/${organizationId}/users`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async updateOrganizationUserRole(organizationId: number, userId: number, roleId: number): Promise<OrganizationUser> {
    return this.request<OrganizationUser>(`/api/Organizations/${organizationId}/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ roleId }),
    });
  }

  async removeOrganizationUser(organizationId: number, userId: number): Promise<void> {
    return this.request<void>(`/api/Organizations/${organizationId}/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async getPendingOrganizationInvitations(): Promise<PendingOrganizationInvitation[]> {
    return this.request<PendingOrganizationInvitation[]>('/api/Organizations/invitations/pending');
  }

  async acceptOrganizationInvitation(organizationId: number): Promise<OrganizationUser> {
    return this.request<OrganizationUser>(`/api/Organizations/${organizationId}/invitations/accept`, {
      method: 'POST',
    });
  }

  async declineOrganizationInvitation(organizationId: number): Promise<void> {
    return this.request<void>(`/api/Organizations/${organizationId}/invitations/decline`, {
      method: 'POST',
    });
  }

  // ── Folders ─────────────────────────────────────────────────────────────────

  async getFoldersByProject(projectId: number): Promise<Folder[]> {
    return this.request<Folder[]>(`/api/folders/project/${projectId}`);
  }

  async getFolderCountByProject(projectId: number): Promise<number> {
    return this.request<number>(`/api/folders/project/${projectId}/count`);
  }

  async getSubfolders(parentFolderId: number): Promise<Folder[]> {
    return this.request<Folder[]>(`/api/folders/${parentFolderId}/subfolders`);
  }

  async getFolderContents(folderId: number): Promise<FolderContents> {
    return this.request<FolderContents>(`/api/folders/${folderId}/contents`);
  }

  async createFolder(command: CreateFolderCommand): Promise<Folder> {
    return this.request<Folder>('/api/folders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(command),
    });
  }

  async updateFolder(id: number, command: UpdateFolderCommand): Promise<Folder> {
    return this.request<Folder>(`/api/folders/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(command),
    });
  }

  async deleteFolder(id: number): Promise<void> {
    return this.request<void>(`/api/folders/${id}`, { method: 'DELETE' });
  }

  // ── Folder Users ─────────────────────────────────────────────────────────────

  async getFolderUsers(folderId: number): Promise<FolderUser[]> {
    return this.request<FolderUser[]>(`/api/folders/${folderId}/users`);
  }

  async assignUserToFolder(folderId: number, userId: number): Promise<FolderUser> {
    return this.request<FolderUser>(`/api/folders/${folderId}/users`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async updateFolderUserRole(folderId: number, userId: number, roleId: number): Promise<FolderUser> {
    return this.request<FolderUser>(`/api/folders/${folderId}/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ roleId }),
    });
  }

  async removeFolderUser(folderId: number, userId: number): Promise<void> {
    return this.request<void>(`/api/folders/${folderId}/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // ── Workstreams ──────────────────────────────────────────────────────────────

  /**
   * Get all workstreams for a project
   * GET /api/workstreams/project/{projectId}
   */
  async getWorkstreamsByProjectId(projectId: number): Promise<WorkstreamDto[]> {
    return this.request<WorkstreamDto[]>(`/api/workstreams/project/${projectId}`);
  }

  /**
   * Get a workstream by ID
   * GET /api/workstreams/{id}
   */
  async getWorkstreamById(id: number): Promise<WorkstreamDto> {
    return this.request<WorkstreamDto>(`/api/workstreams/${id}`);
  }

  async uploadFile(folderId: number, file: File): Promise<FileItem> {
    const url = `${this.baseUrl}/api/folders/${folderId}/files`;
    // Bypass request() — we must NOT set Content-Type so the browser adds the multipart boundary.
    const send = () => {
      const form = new FormData();
      form.append('file', file);
      return fetch(url, { method: 'POST', credentials: 'include', body: form });
    };

    let response = await send();
    if (response.status === 401) {
      const refreshed = await this.tryRefreshToken();
      if (refreshed) {
        response = await send();
      }
    }
    if (!response.ok) {
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        throw new Error('Unauthorized');
      }
      const err = await response.json().catch(() => ({ message: 'An error occurred' }));
      throw new Error(err.message || `HTTP ${response.status}`);
    }
    return response.json() as Promise<FileItem>;
  }

  async deleteFile(folderId: number, fileId: number): Promise<void> {
    await this.request<void>(`/api/folders/${folderId}/files/${fileId}`, {
      method: 'DELETE',
    });
  }

  // ── Organization Payments & Credits ────────────────────────────────────────

  async getCreditPackages(organizationId: number): Promise<CreditPackage[]> {
    return this.request<CreditPackage[]>(`/api/organizations/${organizationId}/credit-packages`);
  }

  async getPayments(
    organizationId: number,
    page = 1,
    pageSize = 20,
    sortBy = 'date',
    sortDirection: 'asc' | 'desc' = 'desc'
  ): Promise<PagedResult<Payment>> {
    return this.request<PagedResult<Payment>>(
      `/api/organizations/${organizationId}/payments?page=${page}&pageSize=${pageSize}&sortBy=${sortBy}&sortDirection=${sortDirection}`
    );
  }

  async createCheckoutSession(organizationId: number, creditPackageId: number): Promise<CheckoutSession> {
    return this.request<CheckoutSession>(`/api/organizations/${organizationId}/checkout`, {
      method: 'POST',
      body: JSON.stringify({ creditPackageId }),
    });
  }

  async cancelPayment(organizationId: number, sessionId: string, rowVersion?: number): Promise<void> {
    await this.request<void>(`/api/organizations/${organizationId}/payments/cancel`, {
      method: 'POST',
      body: JSON.stringify({ sessionId, rowVersion }),
    });
  }

  async getTransferTargets(organizationId: number): Promise<TransferTarget[]> {
    return this.request<TransferTarget[]>(`/api/organizations/${organizationId}/transfer-targets`);
  }

  async transferCredits(
    organizationId: number,
    targetOrganizationId: number,
    amount: number
  ): Promise<TransferCreditsResult> {
    return this.request<TransferCreditsResult>(`/api/organizations/${organizationId}/transfer-credits`, {
      method: 'POST',
      body: JSON.stringify({ targetOrganizationId, amount }),
    });
  }

  // ── Organization Seats ──────────────────────────────────────────────────────

  async getSeatPlans(): Promise<SeatPlan[]> {
    return this.request<SeatPlan[]>('/api/seat-plans');
  }

  async getOrganizationSeatSummary(organizationId: number): Promise<OrganizationSeatSummary> {
    return this.request<OrganizationSeatSummary>(`/api/organizations/${organizationId}/seats`);
  }

  async getSeatAccessStatus(): Promise<HasSeatAccess> {
    return this.request<HasSeatAccess>('/api/seats/access-status');
  }

  async createSeatCheckoutSession(organizationId: number, seatPlanId: number): Promise<CheckoutSession> {
    return this.request<CheckoutSession>(`/api/organizations/${organizationId}/seats/checkout`, {
      method: 'POST',
      body: JSON.stringify({ seatPlanId }),
    });
  }

  async activateSeat(organizationId: number, userId: number): Promise<OrganizationMemberSeatStatus> {
    return this.request<OrganizationMemberSeatStatus>(
      `/api/organizations/${organizationId}/users/${userId}/seat/activate`,
      { method: 'POST' }
    );
  }

  async deactivateSeat(organizationId: number, userId: number): Promise<OrganizationMemberSeatStatus> {
    return this.request<OrganizationMemberSeatStatus>(
      `/api/organizations/${organizationId}/users/${userId}/seat/deactivate`,
      { method: 'POST' }
    );
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
